import PropTypes from "prop-types";
import { useContext, useMemo } from "react";
import { useRouter } from "next/router";
import { useMutation } from "@apollo/client/react/hooks";
import { useFormContext } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { CartContext } from "lib/cartContext";
import { calculateFormTotals } from "helpers/cart";
import { scrollToField, formDataToVariables } from "helpers/product";
import Summary from "./summary/Summary";
import Sticky from "./sticky/Sticky";

import { GetCart } from "../../../helpers/api/cadeauConcepten/queries/cart.gql";
import { AddPackageToCart } from "../../../helpers/api/cadeauConcepten/mutations/cart.gql";

import { SummaryBarStyled } from "./SummaryBar.styled";

function SummaryBar({
    configRef,
    quantityValueRef,
    personalMessageRef,
    packagingRef,
    giftWrapRef,
    upsellRef
}) {
    const [ref, inView] = useInView({
        threshold: 0
    });
    const { showOverlay, setShowOverlay, cartData } = useContext(CartContext);

    const router = useRouter();
    const {
        watch,
        reset,
        getValues,
        formState,
        trigger,
        errors
    } = useFormContext();
    const values = watch();
    const { isDirty } = formState;

    const formValues = useMemo(() => calculateFormTotals(values), [values]);

    function redirectToCheckout() {
        router.push("/checkout");
    }

    const [addPackage, { loading: addPackageLoading }] = useMutation(
        AddPackageToCart,
        {
            errorPolicy: "all",
            update: (cache, { data: { addPackageToCart } }) => {
                if (addPackageToCart) {
                    cache.writeQuery({
                        query: GetCart,
                        data: { cart: { ...addPackageToCart.cart } }
                    });
                }
            },
            onError: () => {
                setShowOverlay(false);
            },
            onCompleted: () => {
                setShowOverlay(false);
                reset({
                    giftWrap: false,
                    packaging: { checked: false, items: [] },
                    personalMessage: {
                        checked: false,
                        message: ""
                    },
                    upsell: {
                        checked: false,
                        items: []
                    }
                });

                redirectToCheckout();
            }
        }
    );

    async function onSubmit(event) {
        event.preventDefault();
        const data = getValues();

        // If form has been touched go to checkout.
        if (isDirty) {
            // Validate field
            const valid = await trigger();

            // If form is valid and variables available add config to cart.
            if (valid) {
                // Parse form data to variables for addback
                const { variables } = formDataToVariables(data);

                if (variables) {
                    setShowOverlay(true);
                    addPackage({
                        variables
                    });
                }
            }
            if (!valid) {
                scrollToField(errors, {
                    configRef,
                    quantityValueRef,
                    personalMessageRef,
                    packagingRef,
                    giftWrapRef,
                    upsellRef
                });
            }
        }

        // If form has not been touched go to checkout.
        if (
            !isDirty &&
            !formState?.isSubmitSuccessful &&
            !formState?.isSubmitSuccessful &&
            !formState?.isSubmitted &&
            !formState?.isSubmitted
        ) {
            redirectToCheckout();
        }
    }

    const formSubtotal = Number(formValues?.subtotal);
    const cartSubtotal = Number(cartData?.cart?.totals?.subtotal ?? 0);
    const cartFormSubtotal = formSubtotal + cartSubtotal;
    let packageQuantity = cartData?.cart?.items?.length ?? 0;

    if (formValues?.quantity > 0) {
        packageQuantity += 1;
    }
    const buttonDisabled = packageQuantity === 0 || addPackageLoading;

    return (
        <>
            <SummaryBarStyled ref={ref}>
                <Summary
                    packageQuantity={packageQuantity}
                    subtotal={cartFormSubtotal}
                    onSubmit={onSubmit}
                    showOverlay={showOverlay}
                    isButtonDisabled={buttonDisabled}
                />
            </SummaryBarStyled>
            {!inView && (
                <Sticky
                    quantity={packageQuantity}
                    subtotal={cartFormSubtotal}
                    buttonDisabled={buttonDisabled}
                    onSubmit={onSubmit}
                />
            )}
        </>
    );
}

SummaryBar.propTypes = {
    giftWrapRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired,
    quantityValueRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired,
    personalMessageRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired,
    packagingRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired,
    upsellRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired,
    configRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]).isRequired
};

export default SummaryBar;
